import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { deleteAllProgramImages } from '@/lib/loyaltyImageCleanup';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Gift, TrendingUp, Coffee, Users, Wallet, Trash2, Plus, Check, AlertTriangle, QrCode, Download, Copy, ExternalLink, RefreshCw, Share, Eye, Settings, Apple, Pencil, Bell } from 'lucide-react';
import { toast } from "@/hooks/ui/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import LoyaltyPassBuilder from './LoyaltyPassBuilder';
import UnifiedPassPreview from './UnifiedPassPreview';
import BusinessScannerCredentialsManager from '@/components/business/BusinessScannerCredentialsManager';
import TemplateSelectionDialog from '@/components/dashboard/TemplateSelectionDialog';
import WalletTemplateManager from '@/components/wallet/WalletTemplateManager';
import { LoyaltyFormCustomizer } from './LoyaltyFormCustomizer';
import BonusPointsDialog from './BonusPointsDialog';
import NotificationModal from './NotificationModal';
import { PRIMARY_COLOR } from '@/lib/constants/colors';
import { UnifiedSkeleton } from '@/components/ui/unified-skeleton';

interface LoyaltyProgram {
  id: string;
  business_id: string;
  name: string;
  description: string;
  program_type: 'points' | 'stamps' | 'visits' | 'dotypos_points';
  points_per_currency?: number;
  points_for_reward?: number;
  stamps_required?: number;
  stamp_expiry_days?: number;
  reward_type: 'percentage' | 'fixed_amount' | 'free_item' | 'custom';
  reward_value?: number;
  reward_description: string;
  minimum_purchase?: number;
  max_points_per_transaction?: number;
  allow_partial_redemption: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // New fields for wallet integration
  template_code?: string;
  qr_code_url?: string;
  pass_distribution_url?: string;
  google_class_id?: string;
  apple_class_id?: string;
  background_color?: string;
  logo_url?: string; // Deprecated - kept for backward compatibility
  apple_logo_urls?: { '1x': string; '2x': string; '3x': string };
  google_logo_url?: string;
  loyalty_points_label?: string;
  member_label?: string;
  hero_image_url?: string; // Deprecated - kept for backward compatibility
  hero_image_urls?: { '1x': string; '2x': string; '3x': string };
  // Bonus points for new customers
  bonus_points_enabled?: boolean;
  bonus_points_amount?: number;
  bonus_points_end_date?: string;
  // Location-based notifications
  location_notifications_enabled?: boolean;
  location_notification_message?: string;
}


interface LoyaltyProgramManagerProps {
  businessId: string | null;
}

const LoyaltyProgramManager: React.FC<LoyaltyProgramManagerProps> = ({ businessId }) => {
  const { session } = useAuth();
  const [programs, setPrograms] = useState<LoyaltyProgram[]>([]);
  const [businessName, setBusinessName] = useState<string>('');
  const [businessData, setBusinessData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState<LoyaltyProgram | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingProgram, setEditingProgram] = useState<LoyaltyProgram | null>(null);

  // localStorage key for builder state
  const BUILDER_STATE_KEY = `loyaltyBuilderState_${businessId}`;

  // Load builder state from localStorage
  const loadBuilderState = () => {
    try {
      const saved = localStorage.getItem(BUILDER_STATE_KEY);
      return saved ? JSON.parse(saved) : { showTemplateBuilder: false, editingProgramId: null };
    } catch (error) {
      console.error('Error loading loyalty builder state:', error);
      return { showTemplateBuilder: false, editingProgramId: null };
    }
  };

  const builderState = loadBuilderState();

  const [showTemplateBuilder, setShowTemplateBuilder] = useState(builderState.showTemplateBuilder);
  const [showTemplateSelection, setShowTemplateSelection] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [programsWithPasses, setProgramsWithPasses] = useState<Set<string>>(new Set());
  const [templatesByProgram, setTemplatesByProgram] = useState<Map<string, any>>(new Map());
  const [passTemplates, setPassTemplates] = useState<any[]>([]);
  const [isQrDialogOpen, setIsQrDialogOpen] = useState(false);
  const [isPassPreviewOpen, setIsPassPreviewOpen] = useState(false);
  const [selectedProgramForPreview, setSelectedProgramForPreview] = useState<LoyaltyProgram | null>(null);
  const [passPreviewType, setPassPreviewType] = useState<'google' | 'apple'>('apple');
  const [programPreviewTypes, setProgramPreviewTypes] = useState<Record<string, 'google' | 'apple'>>({});
  const [showFormCustomizer, setShowFormCustomizer] = useState(false);
  const [selectedProgramForCustomizer, setSelectedProgramForCustomizer] = useState<LoyaltyProgram | null>(null);
  const [showBonusPointsDialog, setShowBonusPointsDialog] = useState(false);
  const [selectedProgramForBonus, setSelectedProgramForBonus] = useState<LoyaltyProgram | null>(null);
  const [showDisableBonusConfirm, setShowDisableBonusConfirm] = useState(false);
  const [programToDisableBonus, setProgramToDisableBonus] = useState<LoyaltyProgram | null>(null);
  const [passStats, setPassStats] = useState<{[key: string]: {google: number, apple: number}}>({});
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [selectedProgramForNotification, setSelectedProgramForNotification] = useState<LoyaltyProgram | null>(null);

  // Save builder state to localStorage
  useEffect(() => {
    const state = {
      showTemplateBuilder,
      editingProgramId: editingProgram?.id || null,
      timestamp: Date.now()
    };

    try {
      localStorage.setItem(BUILDER_STATE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error('Error saving loyalty builder state:', error);
    }
  }, [showTemplateBuilder, editingProgram, BUILDER_STATE_KEY]);


  useEffect(() => {
    // Reset all state when businessId changes
    setPrograms([]);
    setPassTemplates([]);
    setTemplatesByProgram(new Map());
    setProgramsWithPasses(new Set());
    setLoading(true);

    // Reset creation and UI states (but preserve builder state from localStorage)
    setIsCreating(false);
    // Don't reset showTemplateBuilder - it's managed by localStorage
    setShowTemplateSelection(false);
    setSelectedTemplate(null);
    setSelectedProgram(null);
    setIsQrDialogOpen(false);
    setIsPassPreviewOpen(false);
    setSelectedProgramForPreview(null);
    setPassPreviewType('apple');
    setProgramPreviewTypes({});

    if (businessId) {
      fetchBusinessAndPrograms();
    } else {
      setLoading(false);
    }
  }, [businessId]);

  const fetchBusinessAndPrograms = async () => {
    if (!businessId) return;

    try {
      // Fetch business data including location coordinates
      const { data: fetchedBusinessData, error: businessError } = await supabase
        .from('businesses')
        .select('id, name, latitude, longitude, geofence_radius_meters')
        .eq('id', businessId)
        .single();

      if (!businessError && fetchedBusinessData) {
        setBusinessName(fetchedBusinessData.name);
        setBusinessData(fetchedBusinessData);
      }

      // Fetch loyalty programs (exclude soft-deleted)
      const { data, error } = await supabase
        .from('loyalty_programs')
        .select('*')
        .eq('business_id', businessId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPrograms(data || []);

      // Restore editing program from localStorage if it exists
      if (builderState.editingProgramId && data) {
        const programToEdit = data.find(p => p.id === builderState.editingProgramId);
        if (programToEdit) {
          setEditingProgram(programToEdit);
        }
      }

      // Fetch all pass templates for this business
      const { data: allTemplatesData, error: allTemplatesError } = await supabase
        .from('pass_templates')
        .select('id, name, loyalty_program_id, template_code')
        .eq('business_id', businessId);

      if (!allTemplatesError && allTemplatesData) {
        setPassTemplates(allTemplatesData);

        // Check which programs have pass templates and get template info
        const templatesWithPrograms = allTemplatesData.filter(t => t.loyalty_program_id);
        const programsWithPassesSet = new Set(templatesWithPrograms.map(t => t.loyalty_program_id));
        setProgramsWithPasses(programsWithPassesSet);

        // Create a map of program ID to template
        const templateMap = new Map();
        templatesWithPrograms.forEach(template => {
          templateMap.set(template.loyalty_program_id, template);
        });
        setTemplatesByProgram(templateMap);
      }

      // Fetch pass statistics
      await fetchPassStats();
    } catch (error) {
      console.error('Error fetching programs:', error);
      toast.error('Failed to load loyalty programs');
    } finally {
      setLoading(false);
    }
  };

  const fetchPassStats = async () => {
    if (!businessId) return;

    try {
      // First get all loyalty program IDs for this business
      const { data: programsData, error: programsError } = await supabase
        .from('loyalty_programs')
        .select('id')
        .eq('business_id', businessId)
        .is('deleted_at', null);

      if (programsError) throw programsError;

      const programIds = programsData?.map(p => p.id) || [];

      if (programIds.length === 0) {
        setPassStats({});
        return;
      }

      // Now fetch passes for these loyalty programs
      const { data, error } = await supabase
        .from('passes')
        .select('loyalty_program_id, pass_type_identifier, pass_data')
        .in('loyalty_program_id', programIds)
        .not('loyalty_program_id', 'is', null)
        .eq('status', 'active');

      if (error) throw error;

      const stats: {[key: string]: {google: number, apple: number}} = {};

      (data || []).forEach(pass => {
        if (pass.loyalty_program_id) {
          if (!stats[pass.loyalty_program_id]) {
            stats[pass.loyalty_program_id] = { google: 0, apple: 0 };
          }

          // Determine platform from pass_type_identifier or pass_data
          const isGooglePass =
            pass.pass_type_identifier === 'pass.com.tag-innovation.google-wallet' ||
            (pass.pass_data && typeof pass.pass_data === 'object' && pass.pass_data.platform === 'google');

          if (isGooglePass) {
            stats[pass.loyalty_program_id].google++;
          } else {
            stats[pass.loyalty_program_id].apple++;
          }
        }
      });

      setPassStats(stats);
    } catch (error) {
      console.error('Error fetching pass stats:', error);
    }
  };


  const handleTemplateBuilderComplete = async (loyaltyProgram: any) => {
    // Refresh data to show the new program
    await fetchBusinessAndPrograms();
    setShowTemplateBuilder(false);
    setSelectedTemplate(null);

    // Clear builder state from localStorage
    try {
      localStorage.removeItem(BUILDER_STATE_KEY);
    } catch (error) {
      console.error('Error clearing loyalty builder state:', error);
    }

    // Show success message with QR code info
    if (loyaltyProgram.qr_code_url) {
      toast.success('Loyalty program created! Your QR code is ready to share with customers.');
    }
  };

  const handleTemplateSelect = (template: any) => {
    setSelectedTemplate(template);
    setShowTemplateSelection(false);
    setShowTemplateBuilder(true);
  };

  const handleSkipTemplate = () => {
    setSelectedTemplate(null);
    setShowTemplateSelection(false);
    setShowTemplateBuilder(true);
  };

  const handleCreateLoyaltyProgram = () => {
    if (programs.length >= 1) {
      toast.error(
        "Your current plan includes one loyalty program. To add another, please contact us at +421 902 691 939 or tag-innovation1@gmail.com",
        {
          duration: 8000, // Show for 8 seconds
        }
      );
      return;
    }
    setShowTemplateBuilder(true);
  };


  const handleViewQrCode = async (program: LoyaltyProgram) => {
    if (program.template_code) {
      // Use the loyalty program data directly
      const templateData = {
        id: program.id,
        name: program.name,
        template_code: program.template_code,
        qr_code_url: program.qr_code_url
      };
      
      setSelectedTemplate(templateData);
      setIsQrDialogOpen(true);
      
      // Auto-regenerate QR code every time the dialog is opened
      console.log('Auto-regenerating QR code for fresh sharing...');
      await generateQRCodeForProgram(program);
    }
  };

  const handleViewPass = (program: LoyaltyProgram) => {
    setSelectedProgramForPreview(program);
    setIsPassPreviewOpen(true);
  };

  const generateQRCodeForProgram = async (program: LoyaltyProgram) => {
    const baseUrl = 'https://taginn.app';
    const passUrl = `${baseUrl}/get-pass?template=${program.template_code}`;
    
    // Generate QR code using an API with timestamp to ensure freshness
    const timestamp = Date.now();
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(passUrl)}&t=${timestamp}`;
    
    try {
      // Update the loyalty program with the new QR code URL
      const { error } = await supabase
        .from('loyalty_programs')
        .update({ 
          qr_code_url: qrApiUrl,
          pass_distribution_url: passUrl 
        })
        .eq('id', program.id);

      if (error) throw error;

      // Update local state
      setSelectedTemplate(prev => ({ ...prev, qr_code_url: qrApiUrl }));
      
      // Update programs list
      setPrograms(prev => prev.map(p => 
        p.id === program.id 
          ? { ...p, qr_code_url: qrApiUrl, pass_distribution_url: passUrl }
          : p
      ));

      console.log('QR code auto-regenerated successfully!');
    } catch (error) {
      console.error('Error auto-generating QR code:', error);
      // Don't show error toast for auto-generation to avoid annoying the user
    }
  };

  const generateQRCode = async (template: any) => {
    const baseUrl = 'https://taginn.app';
    const passUrl = `${baseUrl}/get-pass?template=${template.template_code}`;
    
    // Generate QR code using an API with timestamp for freshness
    const timestamp = Date.now();
    const qrApiUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(passUrl)}&t=${timestamp}`;
    
    try {
      // Check if this is a loyalty program (has program.id) or a template
      if (template.id && programs.find(p => p.id === template.id)) {
        // This is a loyalty program
        const { error } = await supabase
          .from('loyalty_programs')
          .update({ 
            qr_code_url: qrApiUrl,
            pass_distribution_url: passUrl 
          })
          .eq('id', template.id);

        if (error) throw error;
        
        // Update programs list
        setPrograms(prev => prev.map(p => 
          p.id === template.id 
            ? { ...p, qr_code_url: qrApiUrl, pass_distribution_url: passUrl }
            : p
        ));
      } else {
        // This is a template (legacy support)
        const { error } = await supabase
          .from('pass_templates')
          .update({ qr_code_url: qrApiUrl })
          .eq('id', template.id);

        if (error) throw error;

        // Update templatesByProgram cache
        const updatedTemplate = { ...template, qr_code_url: qrApiUrl };
        setTemplatesByProgram(prev => {
          const newMap = new Map(prev);
          for (const [programId, tmpl] of prev.entries()) {
            if (tmpl.id === template.id) {
              newMap.set(programId, updatedTemplate);
              break;
            }
          }
          return newMap;
        });
      }

      // Update local state
      setSelectedTemplate(prev => ({ ...prev, qr_code_url: qrApiUrl }));

      toast.success('QR code regenerated successfully!');
    } catch (error) {
      console.error('Error generating QR code:', error);
      toast.error('Failed to generate QR code');
    }
  };

  const copyPassUrl = (template: any) => {
    const baseUrl = 'https://taginn.app';
    const passUrl = `${baseUrl}/get-pass?template=${template.template_code}`;
    navigator.clipboard.writeText(passUrl);
    toast.success('Pass URL copied to clipboard!');
  };

  const downloadQRCode = async (template: any) => {
    if (!template.qr_code_url) return;

    try {
      const response = await fetch(template.qr_code_url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${template.name.replace(/\s+/g, '-').toLowerCase()}-qr-code.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('QR Code downloaded successfully');
    } catch (error) {
      console.error('Error downloading QR code:', error);
      toast.error('Failed to download QR code');
    }
  };

  const handleLinkTemplate = async (programId: string, templateId: string | null) => {
    try {
      // If linking to a template, first unlink any other program from that template
      if (templateId) {
        await supabase
          .from('pass_templates')
          .update({ loyalty_program_id: null })
          .eq('id', templateId);
      }

      // If unlinking (templateId is null), find the current template and unlink it
      if (!templateId) {
        await supabase
          .from('pass_templates')
          .update({ loyalty_program_id: null })
          .eq('loyalty_program_id', programId);
      } else {
        // Update the selected template to link to this program
        await supabase
          .from('pass_templates')
          .update({ loyalty_program_id: programId })
          .eq('id', templateId);
      }

      // Refresh the data
      await fetchBusinessAndPrograms();
      toast.success('Template link updated successfully!');
    } catch (error) {
      console.error('Error updating template link:', error);
      toast.error('Failed to update template link');
    }
  };

  const handleEditProgram = (program: LoyaltyProgram) => {
    setEditingProgram(program);
    setShowTemplateBuilder(false); // Close create mode if open
  };

  const handleDeleteProgram = async (programId: string) => {
    if (!confirm('Are you sure you want to delete this loyalty program? This will expire all customer passes. This action cannot be undone.')) {
      return;
    }

    try {
      // Find the program to get its image URLs
      const programToDelete = programs.find(p => p.id === programId);

      // Use soft deletion by updating deleted_at timestamp
      const { error } = await supabase
        .from('loyalty_programs')
        .update({
          deleted_at: new Date().toISOString(),
          is_active: false
        })
        .eq('id', programId);

      if (error) throw error;

      // Expire all customer passes (don't wait - this can run in background)
      supabase.functions
        .invoke('expire-loyalty-passes', {
          body: {
            loyalty_program_id: programId,
            reason: 'Program deleted by business'
          }
        })
        .then(({ data, error: expireError }) => {
          if (expireError) {
            console.error('Error expiring customer passes:', expireError);
          } else {
            console.log('Customer passes expired:', data);
            if (data && (data.apple_passes_updated > 0 || data.google_passes_expired > 0)) {
              toast.success(`Program deleted. ${data.apple_passes_updated + data.google_passes_expired} customer passes expired.`, {
                duration: 6000
              });
            }
          }
        })
        .catch(err => {
          console.error('Error calling expire-loyalty-passes function:', err);
        });

      // Delete all images from storage (don't wait for this to complete)
      if (programToDelete) {
        deleteAllProgramImages(programToDelete).catch(err => {
          console.error('Error deleting program images:', err);
          // Don't show error to user - program is already deleted from their perspective
        });
      }

      // Remove from local state
      setPrograms(programs.filter(p => p.id !== programId));
      toast.success('Program deleted successfully. Customer passes are being expired...');
    } catch (error) {
      console.error('Error deleting program:', error);
      toast.error('Failed to delete program');
    }
  };

  const handleToggleBonusPoints = async (program: LoyaltyProgram, enabled: boolean) => {
    if (enabled) {
      // Open dialog to set bonus points
      setSelectedProgramForBonus(program);
      setShowBonusPointsDialog(true);
    } else {
      // Show confirmation dialog before disabling
      setProgramToDisableBonus(program);
      setShowDisableBonusConfirm(true);
    }
  };

  const confirmDisableBonusPoints = async () => {
    if (!programToDisableBonus) return;

    try {
      const { error } = await supabase
        .from('loyalty_programs')
        .update({
          bonus_points_enabled: false,
          bonus_points_amount: 0,
          bonus_points_end_date: null
        })
        .eq('id', programToDisableBonus.id);

      if (error) throw error;

      // Update local state
      setPrograms(programs.map(p =>
        p.id === programToDisableBonus.id
          ? { ...p, bonus_points_enabled: false, bonus_points_amount: 0, bonus_points_end_date: undefined }
          : p
      ));
      toast.success('Bonus points disabled');
    } catch (error) {
      console.error('Error disabling bonus points:', error);
      toast.error('Failed to disable bonus points');
    } finally {
      setShowDisableBonusConfirm(false);
      setProgramToDisableBonus(null);
    }
  };

  const handleSaveBonusPoints = async (amount: number, endDate: string) => {
    if (!selectedProgramForBonus) return;

    try {
      const { error } = await supabase
        .from('loyalty_programs')
        .update({
          bonus_points_enabled: true,
          bonus_points_amount: amount,
          bonus_points_end_date: endDate
        })
        .eq('id', selectedProgramForBonus.id);

      if (error) throw error;

      // Update local state
      setPrograms(programs.map(p =>
        p.id === selectedProgramForBonus.id
          ? { ...p, bonus_points_enabled: true, bonus_points_amount: amount, bonus_points_end_date: endDate }
          : p
      ));
      toast.success('Bonus points enabled successfully');
      setShowBonusPointsDialog(false);
      setSelectedProgramForBonus(null);
    } catch (error) {
      console.error('Error saving bonus points:', error);
      toast.error('Failed to save bonus points');
    }
  };

  const getProgramIcon = (type: string) => {
    switch (type) {
      case 'points':
        return <TrendingUp className="h-5 w-5" />;
      case 'stamps':
        return <Coffee className="h-5 w-5" />;
      case 'visits':
        return <Users className="h-5 w-5" />;
      default:
        return <Gift className="h-5 w-5" />;
    }
  };

  if (loading) {
    return <UnifiedSkeleton variant="tab" />;
  }

  // Show form customizer if active
  if (showFormCustomizer && selectedProgramForCustomizer) {
    return (
      <LoyaltyFormCustomizer
        program={selectedProgramForCustomizer}
        businessName={selectedProgramForCustomizer.name}
        onComplete={() => {
          setShowFormCustomizer(false);
          setSelectedProgramForCustomizer(null);
          fetchBusinessAndPrograms();
        }}
        onCancel={() => {
          setShowFormCustomizer(false);
          setSelectedProgramForCustomizer(null);
        }}
      />
    );
  }

  // Show loyalty pass builder if in creation or edit mode (only allow if editing or under limit)
  if ((showTemplateBuilder || editingProgram) && (editingProgram || programs.length < 1)) {
    return (
      <LoyaltyPassBuilder
        businessId={businessId || ''}
        editMode={!!editingProgram}
        existingProgram={editingProgram || undefined}
        onComplete={(updatedProgram) => {
          if (editingProgram) {
            // Edit mode - refresh programs list
            fetchBusinessAndPrograms();
            setEditingProgram(null);
            // Clear builder state from localStorage
            try {
              localStorage.removeItem(BUILDER_STATE_KEY);
            } catch (error) {
              console.error('Error clearing loyalty builder state:', error);
            }
          } else {
            // Create mode - use existing handler
            handleTemplateBuilderComplete(updatedProgram);
          }
        }}
        onCancel={() => {
          setShowTemplateBuilder(false);
          setEditingProgram(null);
          setSelectedTemplate(null);
          // Clear builder state from localStorage
          try {
            localStorage.removeItem(BUILDER_STATE_KEY);
          } catch (error) {
            console.error('Error clearing loyalty builder state:', error);
          }
        }}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Customer Loyalty Programs</h2>
      </div>

      <div className="w-full space-y-6">
          {programs.length === 0 ? (
            <div className="flex flex-col items-center justify-center min-h-[400px] space-y-6">
              <Card className="max-w-md">
                <CardContent className="flex flex-col items-center justify-center py-8">
                  <Gift className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground text-center">
                    No loyalty programs yet. Create your first program to start rewarding customers!
                  </p>
                </CardContent>
              </Card>
              <Button 
                className="flex items-center gap-2"
                onClick={handleCreateLoyaltyProgram}
                size="lg"
              >
                <Plus className="h-4 w-4" />
                Create Loyalty Program
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {programs.map((program) => (
                <Card key={program.id} className="overflow-hidden border hover:shadow-lg transition-shadow duration-200">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-primary/10 dark:bg-primary/20 rounded-lg text-primary dark:text-primary">
                        {getProgramIcon(program.program_type)}
                      </div>
                      <div>
                        <CardTitle className="text-base font-semibold">{program.name}</CardTitle>
                        <CardDescription className="text-xs mt-1">
                          {program.description}
                          <span className="ml-2 text-primary">
                            • {(passStats[program.id]?.apple || 0) + (passStats[program.id]?.google || 0)} passes
                          </span>
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleEditProgram(program)}
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 border hover:border-blue-400"
                        title="Edit program"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteProgram(program.id)}
                        className="h-8 w-8 p-0 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 border hover:border-red-400"
                        title="Delete program"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardHeader>
                  
                  <CardContent>
                    {program.template_code ? (
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Left Half: Program Details + Sharing */}
                        <div className="space-y-10">
                          {/* Program Details */}
                          <div className="space-y-4">
                            <h4 className="text-base font-semibold">Program Details</h4>
                            <div className="space-y-3">
                              <div>
                                <span className="text-sm text-muted-foreground">Type:</span>
                                <div className="text-sm font-medium capitalize text-primary">{program.program_type}</div>
                              </div>
                              <div>
                                <div className="text-sm font-medium leading-relaxed">{program.reward_description}</div>
                              </div>
                              {program.program_type === 'points' && (
                                <div>
                                  <span className="text-sm text-muted-foreground">Points System:</span>
                                  <div className="text-sm font-medium">
                                    <div>{program.points_per_currency} points per $1 spent</div>
                                    <div>{program.points_for_reward} points needed for reward</div>
                                  </div>
                                </div>
                              )}
                              {(program.program_type === 'stamps' || program.program_type === 'visits') && (
                                <div>
                                  <span className="text-sm text-muted-foreground">Required:</span>
                                  <div className="text-sm font-medium">{program.stamps_required} {program.program_type}</div>
                                </div>
                              )}
                              <div>
                                <span className="text-sm text-muted-foreground">Distributed Passes:</span>
                                <div className="text-sm font-medium flex items-center gap-3">
                                  <div className="flex items-center gap-1">
                                    <Apple className="h-4 w-4 text-muted-foreground" />
                                    <span>{passStats[program.id]?.apple || 0} Apple</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Wallet className="h-4 w-4 text-muted-foreground" />
                                    <span>{passStats[program.id]?.google || 0} Google</span>
                                  </div>
                                </div>
                              </div>

                              {/* Bonus Points for New Customers */}
                              {(program.program_type === 'points' || program.program_type === 'dotypos_points') && (
                                <div className="pt-2 border-t">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <Gift className="h-4 w-4 text-muted-foreground" />
                                      <span className="text-sm font-medium">Bonus Points for New Users</span>
                                    </div>
                                    <Switch
                                      checked={program.bonus_points_enabled || false}
                                      onCheckedChange={(checked) => handleToggleBonusPoints(program, checked)}
                                    />
                                  </div>
                                  {program.bonus_points_enabled && program.bonus_points_amount && (
                                    <div className="mt-2 text-sm text-muted-foreground pl-6">
                                      <div className="font-medium text-foreground">
                                        {program.bonus_points_amount} bonus points
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        Active until {new Date(program.bonus_points_end_date || '').toLocaleDateString('en-GB').replace(/\//g, '.')}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Customer & Staff Access Section */}
                          <div className="space-y-4">
                            <h4 className="text-base font-semibold">Customer & Staff Access</h4>

                            <div className="flex items-start gap-6">
                              {/* QR Code - Bigger */}
                              <div className="flex-shrink-0">
                                {program.qr_code_url ? (
                                  <img
                                    src={program.qr_code_url}
                                    alt="QR Code"
                                    className="w-36 h-36 rounded-lg border bg-white shadow-sm"
                                  />
                                ) : (
                                  <div className="w-36 h-36 rounded-lg border border-dashed border-muted-foreground/30 bg-muted flex items-center justify-center">
                                    <QrCode className="h-16 w-16 text-muted-foreground" />
                                  </div>
                                )}
                              </div>

                              {/* Action Buttons - Smaller */}
                              <div className="flex-1 space-y-3">
                                <p className="text-sm text-muted-foreground">
                                  Share your loyalty program with customers and set up staff access.
                                </p>
                                <div className="space-y-2">
                                  <Button
                                    size="sm"
                                    onClick={() => handleViewQrCode(program)}
                                    className="w-full bg-primary hover:bg-primary/90"
                                  >
                                    <Share className="h-3 w-3 mr-2" />
                                    Share & Download
                                  </Button>

                                  {program.program_type !== 'dotypos_points' && (
                                    <BusinessScannerCredentialsManager
                                      businessId={businessId!}
                                      onUpdate={() => {
                                        // Refresh programs if needed
                                        fetchBusinessAndPrograms();
                                      }}
                                    />
                                  )}

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedProgramForCustomizer(program);
                                      setShowFormCustomizer(true);
                                    }}
                                    className="w-full"
                                  >
                                    <Settings className="h-3 w-3 mr-2" />
                                    Customize Form
                                  </Button>

                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSelectedProgramForNotification(program);
                                      setShowNotificationModal(true);
                                    }}
                                    className="w-full"
                                  >
                                    <Bell className="h-3 w-3 mr-2" />
                                    Send Notification
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Right Half: Pass Preview */}
                        <div className="-mt-4 space-y-1">
                          <div className="w-full flex justify-center pt-2">
                            <UnifiedPassPreview
                              key={program.id}
                              data={{
                                businessName: businessName,
                                programName: program.name,
                                header: program.description || businessName,
                                loyaltyPointsLabel: program.loyalty_points_label || 'Reward Points',
                                memberLabel: program.member_label || 'Member',
                                backgroundColor: program.background_color || PRIMARY_COLOR,
                                logoUrl: program.google_logo_url || program.logo_url || '',
                                heroImageUrl: program.hero_image_urls?.['3x'] || program.hero_image_url || '',
                                programType: program.program_type as any,
                                rewardDescription: program.reward_description,
                                requiredCount: program.stamps_required || program.points_for_reward || 10
                              }}
                              passData={{
                                apple_logo_urls: program.apple_logo_urls,
                                google_logo_url: program.google_logo_url,
                                hero_image_urls: program.hero_image_urls
                              }}
                              mode="viewing"
                              showPlatformToggle={false}
                              customerName="John Doe"
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <Alert className="border-yellow-200 bg-yellow-50 dark:bg-yellow-900/20">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <AlertDescription className="text-yellow-700 dark:text-yellow-300">
                          <span className="font-medium">Setup incomplete.</span> Complete the setup to enable customer access.
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              ))}
              
              {/* Create button below existing programs */}
              <div className="flex justify-center pt-4">
                <Button 
                  className="flex items-center gap-2"
                  onClick={handleCreateLoyaltyProgram}
                  size="lg"
                >
                  <Plus className="h-4 w-4" />
                  Create Loyalty Program
                </Button>
              </div>
            </div>
          )}

      </div>

      {/* QR Code and Sharing Dialog */}
      <Dialog open={isQrDialogOpen} onOpenChange={setIsQrDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>QR Code & Sharing Options</DialogTitle>
            <DialogDescription>
              {selectedTemplate?.name || 'Loyalty Pass'}
            </DialogDescription>
          </DialogHeader>
          {selectedTemplate && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* QR Code Section */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <QrCode className="h-5 w-5" />
                    QR Code
                  </CardTitle>
                  <CardDescription>
                    Customers scan this QR code to get your loyalty pass
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {selectedTemplate.qr_code_url ? (
                    <div className="text-center">
                      <img 
                        src={selectedTemplate.qr_code_url} 
                        alt="QR Code" 
                        className="mx-auto border rounded-lg p-4 bg-white"
                        style={{ maxWidth: '200px' }}
                      />
                      <div className="flex gap-2 mt-4 justify-center">
                        <Button size="sm" onClick={() => downloadQRCode(selectedTemplate)}>
                          <Download className="h-4 w-4 mr-2" />
                          Download
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => generateQRCode(selectedTemplate)}>
                          <RefreshCw className="h-4 w-4 mr-2" />
                          Regenerate
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center p-8">
                      <p className="text-muted-foreground mb-4">No QR code generated yet</p>
                      <Button onClick={() => generateQRCode(selectedTemplate)}>
                        <QrCode className="h-4 w-4 mr-2" />
                        Generate QR Code
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Sharing Options */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3 flex-shrink-0">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Share className="h-5 w-5" />
                    Sharing Options
                  </CardTitle>
                  <CardDescription>
                    Different ways to share your loyalty pass with customers
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="pass-url">Direct Link</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="pass-url"
                        value={`https://taginn.app/get-pass?template=${selectedTemplate.template_code}`}
                        readOnly
                        className="font-mono text-sm"
                      />
                      <Button size="sm" onClick={() => copyPassUrl(selectedTemplate)}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Share this link directly with customers via email, SMS, or social media
                    </p>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Preview Customer Experience</h4>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        const url = `https://taginn.app/get-pass?template=${selectedTemplate.template_code}`;
                        window.open(url, '_blank');
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Customer Acquisition Page
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">
                      See what customers will see when they scan your QR code
                    </p>
                  </div>

                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-2">Usage Instructions</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Print the QR code on receipts, flyers, or displays</li>
                      <li>• Share the direct link on social media</li>
                      <li>• Email the link to existing customers</li>
                      <li>• Add to your website or digital displays</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pass Preview Dialog */}
      <Dialog open={isPassPreviewOpen} onOpenChange={setIsPassPreviewOpen}>
        <DialogContent className="max-w-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pass Preview</DialogTitle>
            <DialogDescription>
              {selectedProgramForPreview?.name || 'Loyalty Pass Preview'}
            </DialogDescription>
          </DialogHeader>
          {selectedProgramForPreview && (
            <div className="flex justify-center pb-4">
              <UnifiedPassPreview
                data={{
                  businessName: businessName,
                  programName: selectedProgramForPreview.name,
                  header: selectedProgramForPreview.description || businessName,
                  loyaltyPointsLabel: selectedProgramForPreview.loyalty_points_label || 'Reward Points',
                  memberLabel: selectedProgramForPreview.member_label || 'Member',
                  backgroundColor: selectedProgramForPreview.background_color || '#72461d',
                  logoUrl: (passPreviewType === 'google'
                    ? selectedProgramForPreview.google_logo_url
                    : selectedProgramForPreview.apple_logo_urls?.['3x'])
                    || selectedProgramForPreview.logo_url || '',
                  heroImageUrl: selectedProgramForPreview.hero_image_urls?.['3x'] || selectedProgramForPreview.hero_image_url || '',
                  programType: selectedProgramForPreview.program_type as any,
                  rewardDescription: selectedProgramForPreview.reward_description,
                  requiredCount: selectedProgramForPreview.stamps_required || selectedProgramForPreview.points_for_reward || 10
                }}
                mode="viewing"
                showPlatformToggle={true}
                defaultPlatform={passPreviewType}
                customerName="John Doe"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <TemplateSelectionDialog
        open={showTemplateSelection}
        onSelectTemplate={handleTemplateSelect}
        onSkip={handleSkipTemplate}
        onClose={() => setShowTemplateSelection(false)}
      />

      {/* Bonus Points Dialog */}
      <BonusPointsDialog
        open={showBonusPointsDialog}
        onOpenChange={setShowBonusPointsDialog}
        currentAmount={selectedProgramForBonus?.bonus_points_amount}
        currentEndDate={selectedProgramForBonus?.bonus_points_end_date}
        onSave={handleSaveBonusPoints}
      />

      {/* Notification Modal */}
      {selectedProgramForNotification && businessData && (
        <NotificationModal
          open={showNotificationModal}
          onOpenChange={setShowNotificationModal}
          loyaltyProgram={selectedProgramForNotification}
          business={businessData}
        />
      )}

      {/* Disable Bonus Points Confirmation Dialog */}
      <AlertDialog open={showDisableBonusConfirm} onOpenChange={(open) => {
        if (!open) {
          setShowDisableBonusConfirm(false);
          setProgramToDisableBonus(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Bonus Points?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to disable bonus points for new customers? This will remove the current bonus points offer from your loyalty program.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDisableBonusPoints}>
              Disable Bonus Points
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LoyaltyProgramManager;